import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  Header,
  Footer,
  ImageRun,
  SectionType,
  BorderStyle,
  PageNumber,
  Run,
} from "docx";
import { saveAs } from "file-saver";
import { convertContentToBlocks } from "./documentUtils";

export interface DocumentData {
  title?: string;
  client?: string;
  type?: string;
  content?: string;
  sections?: Array<{
    title: string;
    content: string;
    description?: string;
    imageData?: Array<{
      data: string;
      width?: number;
      height?: number;
      position?: {
        x: number;
        y: number;
        page?: number;
        width?: number;
        height?: number;
      };
    }>;
  }>;
  editableSections?: Array<{
    id: string;
    title: string;
    content: string;
    blocks?: Array<{
      type: string;
      content: any;
      items?: string[];
    }>;
  }>;
}

// Enhanced DOCX generation that matches preview page exactly
export const generateEnhancedDOCX = async (
  documentData: DocumentData,
  documentTheme: any,
  filename: string
) => {
  try {
    // No cover page generation - start directly with content



    // Content pages
    const contentChildren: any[] = [];

    // Process sections for content with enhanced markdown handling
    const sections =
      documentData.editableSections || documentData.sections || [];

    console.log("📄 Processing DOCX sections:", sections.length);

    if (sections.length > 0) {
      for (let index = 0; index < sections.length; index++) {
        const section = sections[index];
        console.log(`📝 Processing DOCX section ${index + 1}:`, section.title || 'Untitled');

        // Section title with appropriate spacing and markdown processing
        const sectionTitle =
          section.title || (section as any).name || `Section ${index + 1}`;
        const cleanTitle = sectionTitle
          .replace(/^#+\s*/, "") // Remove # markers
          .replace(/\*\*(.*?)\*\*/g, "$1") // Remove ** markers and keep content
          .replace(/__(.*?)__/g, "$1") // Remove __ markers and keep content
          .replace(/\*(.*?)\*/g, "$1") // Remove * markers and keep content
          .replace(/_(.*?)_/g, "$1") // Remove _ markers and keep content
          .replace(/`(.*?)`/g, "$1") // Remove ` markers and keep content
          .trim(); // Remove markdown

        // Add section title for all sections
        {
          contentChildren.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: cleanTitle,
                  bold: true,
                  size: 32, // Larger size for main section headings
                  color:
                    documentTheme?.headingColor?.replace("#", "") || "1f2937", // Match preview primary heading color
                }),
              ],
              heading: HeadingLevel.HEADING_1,
              spacing: {
                before: index === 0 ? 200 : 600, // Less space for first section, more for subsequent
                after: 300,
                line: 288, // 1.2 line height for headings
              },
              alignment: AlignmentType.LEFT, // Match preview alignment
              border: {
                bottom: {
                  color: documentTheme?.accentColor?.replace("#", "") || "3182ce",
                  space: 1,
                  style: BorderStyle.SINGLE,
                  size: 6,
                },
              },
            })
          );
        }

        // Enhanced content processing using the same logic as preview
        const blocks =
          (section as any).blocks || convertContentToBlocks(section.content || "");

        if (blocks && blocks.length > 0) {
          console.log(`📦 Processing ${blocks.length} blocks for section ${index + 1}`);
          for (const block of blocks) {
            // Skip header blocks that duplicate the section title
            if (block.type === 'header' || block.type === 'heading') {
              const blockTitle = (block.content || '').replace(/^#+\s*/, '').trim();
              const sectionTitleClean = cleanTitle.toLowerCase();
              const blockTitleClean = blockTitle.toLowerCase();

              // Skip if this heading matches the section title (avoid duplication)
              if (blockTitleClean === sectionTitleClean ||
                  blockTitleClean.includes(sectionTitleClean) ||
                  sectionTitleClean.includes(blockTitleClean)) {
                console.log(`⏭️ Skipping duplicate heading: "${blockTitle}"`);
                continue;
              }
            }

            const blockElements = await convertBlockToDOCX(
              block,
              documentTheme
            );
            contentChildren.push(...blockElements);
          }
        } else {
          // Fallback to simple content processing
          const content = section.content || (section as any).description || "";
          if (content) {
            console.log(`📄 Processing simple content for section ${index + 1}`);
            contentChildren.push(
              ...processSimpleContent(content, documentTheme, cleanTitle)
            );
          }
        }

        // Add minimal space between sections (sections will flow continuously)
        if (index < sections.length - 1) {
          contentChildren.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: "",
                }),
              ],
              spacing: {
                after: 200, // Reduced spacing between sections
              },
            })
          );
        }
      }
    } else {
      // No content available
      contentChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: "No content available to display.",
              size: 22,
              color: "666666",
              italics: true,
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: {
            before: 600,
          },
        })
      );
    }

    // No cover page - start directly with content

    // Create the document with proper page structure to eliminate blank pages
    const doc = new Document({
      creator: "ScopingAI",
      title: documentData.title || "Document",
      description: `Generated document for ${documentData.client || "client"}`,
      sections: [
        // Content section - starts page numbering from 1
        {
          properties: {
            type: SectionType.NEXT_PAGE,
            page: {
              margin: {
                top: 1440, // 1 inch
                right: 1440,
                bottom: 1440,
                left: 1440,
              },
              pageNumbers: {
                start: 1, // Content starts at page 1
                formatType: "decimal",
              },
            },
          },
          headers: {
            default: new Header({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: documentData.title || "Document",
                      size: 18,
                      color: "666666",
                    }),
                  ],
                  alignment: AlignmentType.LEFT,
                }),
              ],
            }),
          },
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  children: [
                    new Run({
                      children: [PageNumber.CURRENT],
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                }),
              ],
            }),
          },
          children: contentChildren,
        },
      ],
    });

    // Generate and save the document
    const blob = await Packer.toBlob(doc);
    saveAs(blob, filename);

    return true;
  } catch (error) {
    console.error("Error generating DOCX:", error);
    throw error;
  }
};

// Helper function to convert blocks to DOCX format
const convertBlockToDOCX = async (
  block: any,
  documentTheme: any
): Promise<any[]> => {
  const elements: any[] = [];

  switch (block.type) {
    case "header":
    case "heading":
      const level = Math.min(Math.max(block.level || 2, 1), 6);
      const headingLevel =
        level === 1
          ? HeadingLevel.HEADING_1
          : level === 2
          ? HeadingLevel.HEADING_2
          : level === 3
          ? HeadingLevel.HEADING_3
          : HeadingLevel.HEADING_4;
      const fontSize =
        level === 1 ? 32 : level === 2 ? 28 : level === 3 ? 24 : 22;

      // Clean heading text from ALL markdown syntax - comprehensive cleaning
      const headingText = (block.content || "")
        .replace(/^#+\s*/, "") // Remove # markers
        .replace(/\*\*(.*?)\*\*/g, "$1") // Remove ** markers and keep content
        .replace(/__(.*?)__/g, "$1") // Remove __ markers and keep content
        .replace(/\*(.*?)\*/g, "$1") // Remove * markers and keep content
        .replace(/_(.*?)_/g, "$1") // Remove _ markers and keep content
        .replace(/`(.*?)`/g, "$1") // Remove ` markers and keep content
        .trim();

      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: headingText,
              bold: true,
              size: fontSize,
              color: documentTheme?.headingColor?.replace("#", "") || "1a365d",
            }),
          ],
          heading: headingLevel,
          spacing: {
            before: level === 1 ? 360 : 300, // 0.75rem equivalent for H1, slightly less for others
            after: 200,
            line: 288, // 1.2 line height for headings (240 * 1.2)
          },
          alignment: AlignmentType.LEFT, // Match preview alignment
        })
      );
      break;

    case "text":
      if (block.content && block.content.trim()) {
        elements.push(
          new Paragraph({
            children: parseTextWithFormatting(block.content, documentTheme),
            spacing: {
              before: 120, // 0.5rem equivalent
              after: 120,  // 0.5rem equivalent
              line: 384,   // 1.6 line height (240 * 1.6)
            },
            alignment: AlignmentType.LEFT, // Match preview default alignment
          })
        );
      }
      break;

    case "list":
      const items =
        block.items ||
        (block.content
          ? block.content
              .split("\n")
              .filter((line: string) => line.trim().startsWith("-"))
              .map((line: string) => line.replace(/^-\s*/, ""))
          : []);

      items.forEach((item: string) => {
        elements.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "• ",
                size: 22,
                color: documentTheme?.textColor?.replace("#", "") || "4b5563", // Match preview text color
              }),
              ...parseTextWithFormatting(item, documentTheme),
            ],
            spacing: {
              before: 60,  // 0.25rem equivalent
              after: 60,   // 0.25rem equivalent
              line: 384,   // 1.6 line height
            },
            indent: {
              left: 720, // 0.5 inch
            },
            alignment: AlignmentType.LEFT, // Match preview alignment
          })
        );
      });
      break;

    case "image":
      if (block.content) {
        // Try to embed actual image
        try {
          if (block.content.startsWith("data:image/")) {
            // Handle base64 images
            const base64Data = block.content.split(",")[1];
            const imageBuffer = Buffer.from(base64Data, "base64");

            elements.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    data: imageBuffer,
                    transformation: {
                      width: 400,
                      height: 300,
                    },
                    type: "png",
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: {
                  before: 200,
                  after: 200,
                },
              })
            );

            // Add caption if available
            if (block.caption || block.alt) {
              elements.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: block.caption || block.alt || "",
                      italics: true,
                      size: 18,
                      color: "666666",
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                  spacing: {
                    after: 200,
                  },
                })
              );
            }
          } else if (block.content.startsWith("http")) {
            // Handle URL images - fetch and embed
            try {
              const response = await fetch(block.content);
              const arrayBuffer = await response.arrayBuffer();

              elements.push(
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: arrayBuffer,
                      transformation: {
                        width: 400,
                        height: 300,
                      },
                      type: "png",
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                  spacing: {
                    before: 200,
                    after: 200,
                  },
                })
              );

              // Add caption if available
              if (block.caption || block.alt) {
                elements.push(
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: block.caption || block.alt || "",
                        italics: true,
                        size: 18,
                        color: "666666",
                      }),
                    ],
                    alignment: AlignmentType.CENTER,
                    spacing: {
                      after: 200,
                    },
                  })
                );
              }
            } catch (fetchError) {
              console.warn("Could not fetch image from URL:", fetchError);
              // Fallback to placeholder with border
              elements.push(
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `[Image: ${block.caption || block.alt || "Image"}]`,
                      italics: true,
                      size: 20,
                      color: "3182ce",
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                  spacing: {
                    before: 200,
                    after: 200,
                  },
                  border: {
                    top: {
                      color: "e5e7eb",
                      space: 1,
                      style: BorderStyle.SINGLE,
                      size: 6,
                    },
                    bottom: {
                      color: "e5e7eb",
                      space: 1,
                      style: BorderStyle.SINGLE,
                      size: 6,
                    },
                    left: {
                      color: "e5e7eb",
                      space: 1,
                      style: BorderStyle.SINGLE,
                      size: 6,
                    },
                    right: {
                      color: "e5e7eb",
                      space: 1,
                      style: BorderStyle.SINGLE,
                      size: 6,
                    },
                  },
                })
              );
            }
          } else {
            // Unknown image format - use placeholder
            elements.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: `[Image: ${block.caption || block.alt || "Image"}]`,
                    italics: true,
                    size: 20,
                    color: "666666",
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: {
                  before: 200,
                  after: 200,
                },
              })
            );
          }
        } catch (error) {
          console.warn("Could not process image:", error);
          // Fallback to simple placeholder
          elements.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `[Image: ${block.caption || block.alt || "Image"}]`,
                  italics: true,
                  size: 20,
                  color: "666666",
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: {
                before: 200,
                after: 200,
              },
            })
          );
        }
      }
      break;

    case "blockquote":
      if (block.content && block.content.trim()) {
        elements.push(
          new Paragraph({
            children: [
              new TextRun({
                text: block.content
                  .replace(/^>\s*/, "")
                  .replace(/^#+\s*/, "")
                  .replace(/\*\*(.*?)\*\*/g, "$1")
                  .replace(/__(.*?)__/g, "$1")
                  .replace(/\*(.*?)\*/g, "$1")
                  .replace(/_(.*?)_/g, "$1")
                  .replace(/`(.*?)`/g, "$1")
                  .trim(),
                italics: true,
                color: "888888",
              }),
            ],
            border: {
              left: {
                color: "3182ce",
                space: 1,
                style: BorderStyle.SINGLE,
                size: 8,
              },
            },
            spacing: {
              before: 180,
              after: 180,
              line: 384,
            },
            alignment: AlignmentType.LEFT,
          })
        );
      }
      break;

    default:
      if (block.content && block.content.trim()) {
        elements.push(
          new Paragraph({
            children: parseTextWithFormatting(block.content, documentTheme),
            spacing: {
              before: 120, // 0.5rem equivalent
              after: 120,  // 0.5rem equivalent
              line: 384,   // 1.6 line height
            },
            alignment: AlignmentType.LEFT, // Match preview alignment
          })
        );
      }
      break;
  }

  return elements;
};

// Helper function to parse text with markdown formatting - Enhanced to match preview exactly
const parseTextWithFormatting = (
  text: string,
  documentTheme: any
): TextRun[] => {
  const runs: TextRun[] = [];

  console.log("🔄 Parsing text with formatting:", text.substring(0, 100) + "...");

  // Ensure we have a string to work with
  if (typeof text !== "string") {
    if (text === null || text === undefined) {
      return [new TextRun({ text: "", size: 22 })];
    }
    text = String(text);
  }

  // Process markdown using the same logic as the preview's processMarkdownFormatting
  // This ensures perfect consistency between preview and DOCX export

  let currentIndex = 0;
  const textLength = text.length;

  while (currentIndex < textLength) {
    // Find the next markdown pattern
    let nextBoldMatch = text.substring(currentIndex).match(/\*\*(.*?)\*\*/);
    let nextBoldUnderscoreMatch = text.substring(currentIndex).match(/__(.*?)__/);
    let nextItalicMatch = text.substring(currentIndex).match(/\*(.*?)\*/);
    let nextItalicUnderscoreMatch = text.substring(currentIndex).match(/_(.*?)_/);
    let nextCodeMatch = text.substring(currentIndex).match(/`(.*?)`/);

    // Find the earliest match
    let earliestMatch = null;
    let earliestIndex = textLength;
    let matchType = '';

    if (nextBoldMatch && nextBoldMatch.index !== undefined) {
      const matchIndex = currentIndex + nextBoldMatch.index;
      if (matchIndex < earliestIndex) {
        earliestMatch = nextBoldMatch;
        earliestIndex = matchIndex;
        matchType = 'bold';
      }
    }

    if (nextBoldUnderscoreMatch && nextBoldUnderscoreMatch.index !== undefined) {
      const matchIndex = currentIndex + nextBoldUnderscoreMatch.index;
      if (matchIndex < earliestIndex) {
        earliestMatch = nextBoldUnderscoreMatch;
        earliestIndex = matchIndex;
        matchType = 'bold';
      }
    }

    if (nextItalicMatch && nextItalicMatch.index !== undefined) {
      const matchIndex = currentIndex + nextItalicMatch.index;
      // Make sure this isn't part of a bold pattern (**text**)
      if (matchIndex < earliestIndex && !text.substring(matchIndex - 1, matchIndex + nextItalicMatch[0].length + 1).match(/\*\*.*\*\*/)) {
        earliestMatch = nextItalicMatch;
        earliestIndex = matchIndex;
        matchType = 'italic';
      }
    }

    if (nextItalicUnderscoreMatch && nextItalicUnderscoreMatch.index !== undefined) {
      const matchIndex = currentIndex + nextItalicUnderscoreMatch.index;
      // Make sure this isn't part of a bold pattern (__text__)
      if (matchIndex < earliestIndex && !text.substring(matchIndex - 1, matchIndex + nextItalicUnderscoreMatch[0].length + 1).match(/__.*__/)) {
        earliestMatch = nextItalicUnderscoreMatch;
        earliestIndex = matchIndex;
        matchType = 'italic';
      }
    }

    if (nextCodeMatch && nextCodeMatch.index !== undefined) {
      const matchIndex = currentIndex + nextCodeMatch.index;
      if (matchIndex < earliestIndex) {
        earliestMatch = nextCodeMatch;
        earliestIndex = matchIndex;
        matchType = 'code';
      }
    }

    if (earliestMatch && earliestIndex < textLength) {
      // Add any regular text before the match
      if (earliestIndex > currentIndex) {
        const regularText = text.substring(currentIndex, earliestIndex);
        if (regularText) {
          runs.push(
            new TextRun({
              text: regularText,
              size: 22,
              color: documentTheme?.textColor?.replace("#", "") || "2d3748",
            })
          );
        }
      }

      // Add the formatted text
      const matchedContent = earliestMatch[1];
      console.log(`🔤 ${matchType} text found: "${matchedContent}"`);

      if (matchType === 'bold') {
        runs.push(
          new TextRun({
            text: matchedContent,
            bold: true,
            size: 22,
            color: documentTheme?.textColor?.replace("#", "") || "2d3748",
          })
        );
      } else if (matchType === 'italic') {
        runs.push(
          new TextRun({
            text: matchedContent,
            italics: true,
            size: 22,
            color: documentTheme?.textColor?.replace("#", "") || "2d3748",
          })
        );
      } else if (matchType === 'code') {
        runs.push(
          new TextRun({
            text: matchedContent,
            font: "Courier New",
            size: 20,
            color: "333333",
          })
        );
      }

      // Move past this match
      currentIndex = earliestIndex + earliestMatch[0].length;
    } else {
      // No more matches, add the rest as regular text
      const remainingText = text.substring(currentIndex);
      if (remainingText) {
        runs.push(
          new TextRun({
            text: remainingText,
            size: 22,
            color: documentTheme?.textColor?.replace("#", "") || "2d3748",
          })
        );
      }
      break;
    }
  }

  // If no formatting was found, return the original text as a single run
  if (runs.length === 0) {
    runs.push(
      new TextRun({
        text: text,
        size: 22,
        color: documentTheme?.textColor?.replace("#", "") || "2d3748",
      })
    );
  }

  console.log(`✅ Created ${runs.length} text runs from formatted text`);
  return runs;
};

// Helper function for simple content processing (fallback)
const processSimpleContent = (content: string, documentTheme: any, sectionTitle?: string): any[] => {
  const elements: any[] = [];
  const paragraphs = content.split("\n\n");

  console.log("📄 Processing simple content with duplicate prevention...");

  paragraphs.forEach((paragraph: string, index: number) => {
    if (paragraph.trim()) {
      console.log(`📝 Processing paragraph ${index + 1}: ${paragraph.substring(0, 50)}...`);

      // Check if it's a heading (starts with #)
      if (paragraph.trim().startsWith("#")) {
        const headingText = paragraph
          .replace(/^#+\s*/, "") // Remove # markers
          .replace(/\*\*(.*?)\*\*/g, "$1") // Remove ** markers and keep content
          .replace(/__(.*?)__/g, "$1") // Remove __ markers and keep content
          .replace(/\*(.*?)\*/g, "$1") // Remove * markers and keep content
          .replace(/_(.*?)_/g, "$1") // Remove _ markers and keep content
          .replace(/`(.*?)`/g, "$1") // Remove ` markers and keep content
          .trim();

        // Skip if this heading matches the section title (avoid duplication)
        if (sectionTitle) {
          const headingLower = headingText.toLowerCase();
          const sectionLower = sectionTitle.toLowerCase();
          if (headingLower === sectionLower ||
              headingLower.includes(sectionLower) ||
              sectionLower.includes(headingLower)) {
            console.log(`⏭️ Skipping duplicate heading in simple content: "${headingText}"`);
            return;
          }
        }

        elements.push(
          new Paragraph({
            children: [
              new TextRun({
                text: headingText,
                bold: true,
                size: 24,
                color:
                  documentTheme?.subheadingColor?.replace("#", "") ||
                  documentTheme?.textColor?.replace("#", "") ||
                  "374151", // Match preview secondary heading color
              }),
            ],
            heading: HeadingLevel.HEADING_2,
            spacing: {
              before: 300,
              after: 200,
              line: 288, // 1.2 line height for headings
            },
            alignment: AlignmentType.LEFT, // Match preview alignment
          })
        );
      } else {
        // Regular paragraph with enhanced formatting to match preview
        console.log(`📄 Processing regular paragraph with markdown formatting`);
        elements.push(
          new Paragraph({
            children: parseTextWithFormatting(paragraph.trim(), documentTheme),
            spacing: {
              before: 120, // 0.5rem equivalent
              after: 120,  // 0.5rem equivalent
              line: 384,   // 1.6 line height
            },
            alignment: AlignmentType.LEFT, // Match preview default alignment
          })
        );
      }
    }
  });

  console.log(`✅ Created ${elements.length} elements from simple content`);
  return elements;
};

// Keep the original function for backward compatibility
export const generateDOCX = generateEnhancedDOCX;
